import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { FormInput } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import Alert from "@/components/Base/Alert";
import axios from "axios";

type EditTeamCategoryProps = {
  mode?: "add" | "edit";
};

interface FormData {
  teamCategory: string;
  companyId?: string;
}

interface FormErrors {
  teamCategory?: string;
}

const EMPTY_FORM: FormData = {
  teamCategory: "",
};

function EditTeamCategory({ mode = "edit" }: EditTeamCategoryProps) {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === "edit");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    const init = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      try {
        setLoading(true);

        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");

        if (mode === "edit" && id) {
          const catRes = await axios.get(`${apiUrl}/team-categories/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const cat = catRes.data;

          setFormData({
            teamCategory: cat.teamCategory || "",
            companyId: cat.companyId || "",
          });
        } else if (companyId) {
          setFormData((prev) => ({ ...prev, companyId }));
        }
      } catch (error) {
        console.error("Failed to initialize:", error);
        setSubmitError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [mode, id]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.teamCategory.trim()) {
      newErrors.teamCategory = "Category name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      const apiData = {
        teamCategory: formData.teamCategory,
        companyId: formData.companyId || undefined,
      };

      if (mode === "add") {
        const queryParams = new URLSearchParams();
        if (formData.companyId) queryParams.append("companyId", formData.companyId);

        await axios.post(`${apiUrl}/team-categories?${queryParams.toString()}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Team category created successfully!");
      } else {
        await axios.put(`${apiUrl}/team-categories/${id}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Team category updated successfully!");
      }

      setTimeout(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/team-categories";
        if (projectId) {
          redirectUrl += `?projectId=${projectId}`;
        } else if (companyId) {
          redirectUrl += `?companyId=${companyId}`;
        }

        navigate(redirectUrl);
      }, 1500);
    } catch (error: any) {
      console.error("Submit failed:", error);
      const message = error.response?.data?.message || error.message || "An error occurred";
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = searchParams.get("projectId");
    const companyId = formData.companyId;

    let redirectUrl = "/team-categories";
    if (projectId) {
      redirectUrl += `?projectId=${projectId}`;
    } else if (companyId) {
      redirectUrl += `?companyId=${companyId}`;
    }

    navigate(redirectUrl);
  };

  if (loading && mode === "edit") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
          <div>Loading team categories...</div>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">
                {mode === "add" ? "Add Team Category" : "Edit Team Category"}
              </div>
            </div>

            <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-10 gap-y-7 lg:gap-y-10 gap-x-6">
              <div className="relative flex flex-col col-span-12 lg:col-span-9 xl:col-span-8 gap-y-7">
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col p-5 box box--stacked">
                    <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                      <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                        <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                        Team Category Information
                      </div>

                      {submitError && (
                        <Alert variant="outline-danger" className="mt-5">
                          <Lucide icon="AlertTriangle" className="w-6 h-6" />
                          {submitError}
                        </Alert>
                      )}

                      {submitSuccess && (
                        <Alert variant="outline-success" className="mt-5">
                          <Lucide icon="CheckCircle" className="w-6 h-6" />
                          {submitSuccess}
                        </Alert>
                      )}

                      <div className="mt-5 grid grid-cols-12 gap-4">
                        <div className="col-span-12">
                          <label className="inline-block mb-2">
                            Category Name <span className="text-danger">*</span>
                          </label>
                          <FormInput
                            type="text"
                            placeholder="Enter category name"
                            value={formData.teamCategory}
                            onChange={(e) => handleInputChange("teamCategory", e.target.value)}
                            className={errors.teamCategory ? "border-danger" : ""}
                          />
                          {errors.teamCategory && <div className="mt-2 text-danger text-xs">{errors.teamCategory}</div>}
                        </div>
                      </div>

                      <div className="flex justify-end mt-8 gap-3">
                        <Button type="button" variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                          {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                          {mode === "add" ? "Create Team Category" : "Update Team Category"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

export default EditTeamCategory;

